// The Devices page: this browser's device identity, linking, approvals and the device table.
import type { ReactNode } from "react";

import type { DeviceKind } from "../../../proto/recipients";
import type { RelayStatus } from "../../../relay/client";
import { plural, rich } from "../../helpers";

const p = plural("en");

export default {
  page: {
    description: "Phones and linked browsers hold the keys your conversations are encrypted to. The host holds none of them.",
    all: "All devices",
    loadFailed: "Could not load the devices",
    none: "No devices",
    noneHint: "Link this browser above, or enrol a phone. Devices hold the keys conversations are encrypted to.",
  },
  kinds: {
    iphone: "iPhone app",
    ipad: "iPad app",
    browser: "Linked browser",
    recovery: "Recovery key",
    android: "Android app",
  } satisfies Record<DeviceKind, string>,
  states: { active: "Active", pending: "Pending", revoked: "Revoked" },
  relay: { idle: "idle", connecting: "connecting", open: "open", closed: "closed" } satisfies Record<RelayStatus, string>,
  table: {
    device: "Device",
    thisBrowser: "This browser",
    added: (kind: string, date: string) => `${kind} · added ${date}`,
    fingerprint: "Fingerprint",
    accountKey: "Account key",
    holdsIt: "Holds it",
    inSignedList: "In the signed list",
    lastSeen: "Last seen",
    state: "State",
  },
  browser: {
    checking: "Checking this browser's keys…",
    label: "This browser",
    forgotten: "Browser keys forgotten",
    linked: "This browser is linked",
    active: "Active",
    linkedDetail: (name: string, relay: ReactNode) =>
      rich(name, " · relay ", relay, " · its private keys live in this browser's IndexedDB and never leave it."),
    deviceNote: (id: ReactNode) =>
      rich("Device ", id, ". Session token held in memory only. Forgetting the browser keeps the account key, if it holds one."),
    forget: "Forget this browser",
    waiting: "Waiting for approval",
    waitingNote: (name: ReactNode) =>
      rich(
        "This browser is enrolled as ",
        name,
        " but cannot open a session until one of your active devices approves it. Check that the approving device shows exactly this fingerprint:",
      ),
    differ: "If they differ, do not approve: someone else made this request.",
    rejected: "This browser was rejected or revoked; link it again to ask for approval.",
  },
  link: {
    form: "Link this browser",
    title: "Link this browser as a device",
    lead: "Gives this browser its own keys so it can read conversations and answer questions.",
    leadLater: "You already have an active device: it will need to approve this browser.",
    leadFirst:
      "This is your first device, so it needs your one-time device code: shown when you accepted your invitation, or issued by an administrator (the owner can also run ocs devices bootstrap-code on the host).",
    firstSteps: ["Get your one-time device code", "Type the code", "Name this browser and link it"],
    laterSteps: ["Link this browser here", "Approve it on an active device", "Check both show the same fingerprint"],
    notLinked: "Not linked",
    code: "One-time device code",
    name: "Device name",
    submit: "Link this browser",
    keysNote:
      "The keys are created here, in WebCrypto, and cannot be exported. Conversations started after linking are encrypted to them; earlier ones stay unreadable here.",
    codeNote: " The code works once and only for your account.",
    defaultName: "Browser",
  },
  pending: {
    label: "Devices waiting for approval",
    title: "Waiting for your approval",
    count: (n: number) => p(n, { other: "# pending" }),
    requested: (kind: string, when: string) => `${kind} · requested ${when}`,
    reject: "Reject",
    approve: "Approve",
    approved: (name: string) => `${name} approved`,
    rejected: (name: string) => `${name} rejected`,
    shouldShow: "Its screen should show:",
    note: "Approve a device only if its screen shows this fingerprint. Approving lets it open a session and sign; to encrypt new conversations to it, the account key signs it into the device list (Approve devices, where the key is held).",
  },
};
